import { useCallback, useState } from "react";
import type { VehicleCreatePayload } from "@/customTypings/vehicle/Vehicle";
import type { VehicleDeletedRestoreAvailableConflict } from "@/utils/vehicle/vehicleCreateConflict";
import {
    VEHICLE_CREATE_CONFLICT,
    VehicleCreateConflictError,
    isVehicleDeletedRestoreAvailableConflict,
} from "@/utils/vehicle/vehicleCreateConflict";
import { DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE, showToastError, showToastSuccess } from "@/utils";

type UseVehicleRestoreOnCreateConflictOptions<TDetail extends { id: string }> = {
    restoreDeletedWithCreateValues: (payload: VehicleCreatePayload & { id: string }) => Promise<TDetail>;
    onRestored: (detail: TDetail) => void;
};

export function useVehicleRestoreOnCreateConflict<TDetail extends { id: string }>({
    restoreDeletedWithCreateValues,
    onRestored,
}: UseVehicleRestoreOnCreateConflictOptions<TDetail>) {
    const [restoreConflict, setRestoreConflict] =
        useState<VehicleDeletedRestoreAvailableConflict | null>(null);
    const [pendingRestoreForm, setPendingRestoreForm] = useState<VehicleCreatePayload | null>(null);
    const [restoringDeleted, setRestoringDeleted] = useState(false);

    const handleCreateError = useCallback((err: unknown): boolean => {
        if (err instanceof VehicleCreateConflictError) {
            if (
                isVehicleDeletedRestoreAvailableConflict(
                    err.conflict,
                    VEHICLE_CREATE_CONFLICT.deletedRestoreAvailable
                )
            ) {
                setRestoreConflict(err.conflict);
                return true;
            }
        }
        return false;
    }, []);

    const clearRestoreConflict = useCallback(() => {
        setRestoreConflict(null);
        setPendingRestoreForm(null);
    }, []);

    const confirmRestore = useCallback(
        async (confirmed: boolean) => {
            if (!confirmed || !restoreConflict || !pendingRestoreForm) {
                clearRestoreConflict();
                return;
            }
            setRestoringDeleted(true);
            try {
                const restored = await restoreDeletedWithCreateValues({
                    id: restoreConflict.existingId,
                    ...pendingRestoreForm,
                });
                const label = restoreConflict.existingRegistration;
                clearRestoreConflict();
                showToastSuccess(`"${label}" restored successfully!`);
                onRestored(restored);
            } catch {
                showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
            } finally {
                setRestoringDeleted(false);
            }
        },
        [restoreConflict, pendingRestoreForm, restoreDeletedWithCreateValues, onRestored, clearRestoreConflict]
    );

    const restoreModalProps = {
        show: restoreConflict !== null && pendingRestoreForm !== null,
        title: "Restore deleted vehicle?",
        body: restoreConflict
            ? "A deleted vehicle with this registration already exists. Restore it using the details you entered?"
            : "",
        buttonSuccessText: restoringDeleted ? "Restoring…" : "Yes",
        buttonCancelText: "No" as const,
    };

    return {
        handleCreateError,
        setPendingRestoreForm,
        confirmRestore,
        restoreModalProps,
    };
}
